import { IFlash } from '@/types';

export type ToastKind = 'done' | 'note' | 'problem';
export type HoldSource = 'focus' | 'hover';

export interface IToast {
  id: number;
  kind: ToastKind;
  message: string;
  title: string;
}

export interface ITimers {
  clear: (handle: number) => void;
  set: (fn: () => void, ms: number) => number;
}

export interface IToaster {
  add: (flash: IFlash) => void;
  close: (id: number) => void;
  hold: (id: number, source: HoldSource) => void;
  release: (id: number, source: HoldSource) => void;
  shown: () => IToast[];
}

// The only place toast close times are set. null: stays until closed.
export const AUTO_CLOSE_MS: Readonly<Record<ToastKind, number | null>> = {
  done: 4000,
  note: 4000,
  problem: null,
};
export const MAX_SHOWN = 3;

const KIND_OF_VARIANT: Readonly<Record<string, ToastKind>> = {
  danger: 'problem',
  info: 'note',
  primary: 'note',
  secondary: 'note',
  success: 'done',
  warning: 'problem',
};

// Old bootstrap variants to the 3 kinds; no variant was 'success' before, so it stays Done.
export const toastKind = (variant?: string): ToastKind => {
  if (variant === undefined) return 'done';
  return KIND_OF_VARIANT[variant] ?? 'note';
};

const isSame = (a: IToast, b: IToast): boolean => a.title === b.title && a.message === b.message;

/** The toast queue: at most MAX_SHOWN at once, newest last, the rest waiting; onChange gets the shown list. */
class Toaster implements IToaster {
  private holds = new Map<number, Set<HoldSource>>();

  private nextId = 1;

  private list: IToast[] = [];

  private timerOf = new Map<number, number>();

  private onChange: (shown: IToast[]) => void;

  private timers: ITimers;

  private waiting: IToast[] = [];

  constructor(timers: ITimers, onChange: (shown: IToast[]) => void) {
    this.onChange = onChange;
    this.timers = timers;
  }

  add(flash: IFlash): void {
    const toast: IToast = {
      id: this.nextId,
      kind: toastKind(flash.variant),
      message: flash.message,
      title: flash.title,
    };
    const repeat = this.list.find((t) => isSame(t, toast));
    if (repeat) {
      this.startTimer(repeat);
      return;
    }
    if (this.waiting.some((t) => isSame(t, toast))) return;
    this.nextId += 1;
    if (this.list.length < MAX_SHOWN) this.show(toast);
    else this.waiting.push(toast);
    this.onChange(this.shown());
  }

  close(id: number): void {
    if (!this.list.some((t) => t.id === id)) return;
    this.stopTimer(id);
    this.holds.delete(id);
    this.list = this.list.filter((t) => t.id !== id);
    const next = this.waiting.shift();
    if (next) this.show(next);
    this.onChange(this.shown());
  }

  hold(id: number, source: HoldSource): void {
    const holds = this.holds.get(id);
    if (!holds) return;
    holds.add(source);
    this.stopTimer(id);
  }

  release(id: number, source: HoldSource): void {
    const toast = this.list.find((t) => t.id === id);
    if (!toast || !this.holds.get(id)?.delete(source)) return;
    this.startTimer(toast);
  }

  shown(): IToast[] {
    return [...this.list];
  }

  private show(toast: IToast): void {
    this.list = [...this.list, toast];
    this.holds.set(toast.id, new Set());
    this.startTimer(toast);
  }

  private startTimer(toast: IToast): void {
    this.stopTimer(toast.id);
    const ms = AUTO_CLOSE_MS[toast.kind];
    if (ms === null || (this.holds.get(toast.id)?.size ?? 0) > 0) return;
    this.timerOf.set(
      toast.id,
      this.timers.set(() => this.close(toast.id), ms),
    );
  }

  private stopTimer(id: number): void {
    const handle = this.timerOf.get(id);
    if (handle !== undefined) this.timers.clear(handle);
    this.timerOf.delete(id);
  }
}

export const createToaster = (timers: ITimers, onChange: (shown: IToast[]) => void): IToaster =>
  new Toaster(timers, onChange);
